import { Component, DestroyRef, ElementRef, Input, OnInit, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { EntryService } from '../entry.service';
import { UtilService } from '../../util.service';
import { SummaryText, SummaryData, TedDomain, ProviderData } from '../structure-data.models';
import { EntryData, PlddtData, Structure } from '../../entry/structure-data.models';
import { GoogleAnalyticsService } from '../../google-analytics.service';
import { CommonService } from '../../common.service';
import { ConfigService } from '../../config.service';
import { PlatformService } from '../../platform.service';
import { MolstarPluginService } from "../../molstar-plugin-service";
import {
  applyAlphaMissenseTooltips,
  colorByAlphaMissense,
  resetColoring,
  resetTooltips
} from '../../molstar-alpha-missense';
import { colorByTed } from '../../molstar-alpha-ted';
import { catchError, distinctUntilChanged, filter, map, of, take, timer } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import {  PaeSelectedRegions } from 'src/app/pae-view/pae-view.component';
import { PaeDataService, Chain} from 'src/app/pae-view/pae-data.service';
import { LegendsPanelComponent } from '../legends-panel/legends-panel.component';
@Component({
  selector: 'summary-tab',
  templateUrl: './summary-tab.component.html',
  styleUrl: './summary-tab.component.css',
  standalone: false
})
export class SummaryTabComponent implements OnInit {

  constructor(
    private store: Store,
    private entryService: EntryService,
    private utilService: UtilService,
    public commonService: CommonService,
    private gaService: GoogleAnalyticsService,
    private configService: ConfigService,
    private platform: PlatformService,
    private el: ElementRef,
    private molstarPluginService: MolstarPluginService,
    private paeDataService: PaeDataService,
  ) { 
    effect(() => {
      this.proteinChains.set(this.paeDataService.chains());
    });
  
  }

  public data: SummaryData;
  public proteinChains = signal<Chain[]>([]);
  public pdbeMolstar: any;
  public accession = toSignal(this.store.select(EntrySelectors.accession));
  public entryData = signal<EntryData>({} as EntryData);
  public complexData = signal<any>({});
  public complexDataList = signal<any[]>([]);
  public alsoFoundInData = signal<any[]>([]);
  public isAlsoFoundInLoading = signal<boolean>(false);
  public alsoFoundInCount = signal<number | null>(null);
  private alsoFoundInCache = new Map<string, number>();
  private activeAlsoFoundInAccession = '';
  public uniprotData = signal<any>({});
  public isBiologicalFunctionLoading = signal<boolean>(false);
  public tedDomainData = toSignal(this.store.select(EntrySelectors.tedDomainData));
  public csvAmData = toSignal(this.store.select(EntrySelectors.csvAmData));
  // Each row's own correct per-chain sequence (same source MacromoleculesTabComponent
  // uses) — needed by getChainWiseSequenceText below, since a chain's own sequence can't
  // be reconstructed by slicing entryData().sequence (see AFPDBE-1418).
  public macromoleculesData = toSignal(this.store.select(EntrySelectors.macromoleculesData));

  // Components list for the "Components" section below, built from macromoleculesData (the
  // prediction API's own per-chain rows) instead of complexData's complexComposition. The
  // complex API has no residue-range field, and its uniprotAccession array is de-duplicated
  // per unique accession while complexComposition has one row per chain — indexing them
  // together misaligns as soon as two chains share an accession (AFPDBE-1418), which is
  // exactly the polyprotein-cleavage case (AFPDBE-1435, e.g. NS3/NS5 both under one
  // accession with different residue ranges). macromoleculesData carries each chain's own
  // accession/description/range already paired correctly, so no index-matching is needed.
  public summaryComponents = computed(() => {
    const groups = this.utilService.groupMacromoleculeChains(this.macromoleculesData() || []);

    return groups.map((group) => {
      const representative = group[0];
      const accession = representative?.uniprotAccession || '';
      // Show the residue range whenever this component is a fragment of a larger UniProt
      // protein (start !== 1) — not just when it collides with a same-accession sibling
      // (AFPDBE-1445 follow-up: one consistent rule wherever a UniProt accession is shown).
      const range = this.utilService.getMacromoleculeResidueRange(representative);
      return {
        identifier: accession,
        stoichiometry: group.length,
        uniprotDescription: representative?.uniprotDescription,
        uniprotAccession: accession,
        range: range && range.start !== 1 ? range : null,
      };
    });
  });

  public providerData = signal<ProviderData>({} as ProviderData);

  public isTedDomain = signal<boolean>(false);
  private readonly destroyRef = inject(DestroyRef);
  public isCopySequence: boolean = false;

  public infoSectionHeight: any = { maxHeight: '1000px', overflow: 'hidden' };
  public showInfoSection = true;
  public kbCount = 0;
  public complexKBCount = 0;
  public summaryTexts: any[][] = [];
  private summaryGroupSize = 5; // or 5, or any number

  public uniprotBaseUrl = this.configService.getConfig().uniprotEntryUrl;
  public structure: Structure;
  public lastUpdateDate: string;
  public molLoadComplete: boolean = false;
  public isAlphaMis: boolean = false;
  public isModalConf: boolean = true;

  public rangeFilter: any;
  /* AlphaMissense Variables */
  public csvAMFileUrl: string;
  public hydrationReady: boolean = false;
  public plddtData: PlddtData;
  public isOldUniprotEntry: boolean = false;
  public otherOrganismScientificNames;
  public curatedComplexId: string = '';

  public subSelectionData = signal<any[]>([]);
  public subSelectionOptions = signal<string[]>([]);
  public currentSelection = signal<string | null>(null);
  public zoomOutDuration = 600;
  private zoomSelectionMutex = Promise.resolve();
  private paeSelectionActive = false;

  @ViewChild(LegendsPanelComponent) legendsPanel!: LegendsPanelComponent;

  ngOnInit(): void {

    this.store.select(EntrySelectors.state)
      .pipe(
        filter((state) => {
          const entryData = state?.entryData;
          const complexData = state?.complexData;

          // 1. Check Entry Data
          if (!entryData || !entryData.modelEntityId) return false;

          // 2. Check Complex Data
          // Since we changed initial state to undefined, 
          // !!complexData will be false until the API returns.
          if (entryData.isComplex) {
            // If it's a complex, we still want to ensure we have the ID
            if (!complexData || !complexData.modelEntityId) {
              return false;
            }
            return true;
          }

          // If not a complex, wait for complex request completion and UniProt when needed.
          if (complexData === undefined) {
            return false;
          }
          return true;
        }),

        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((state) => {
        const entryData = state.entryData;
        const complexData = state.complexData;
        const complexDataList = state.complexDataList;
        const uniprotData = state.uniprotData;
        const providerData = state.providerData;

        this.entryData.set(entryData)
        this.complexData.set(complexData)
        this.complexDataList.set(complexDataList || []);
        this.alsoFoundInData.set(complexDataList || []);
        this.uniprotData.set(uniprotData);
        this.isBiologicalFunctionLoading.set(this.shouldShowBiologicalFunctionLoader(uniprotData));
        this.providerData.set(providerData);

        this.createSummaryData();
        this.getPdbeData();
        if(this.entryData().isComplex){
          // this.getExperimentalStructuresText();
          this.getCuratedComplexText();
        }

        if (!this.entryData().isComplex) {
          const accession = this.entryData()?.uniprotAccession;
          if (accession) {
            this.loadAlsoFoundInCount(Array.isArray(accession) ? accession[0] : accession);
          }
        }
       
        this.initMolstar();
      });

          this.store.select(EntrySelectors.uniprotData)
            .pipe(
              filter(() => !!this.entryData()?.modelEntityId),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((uniprotData) => {
              this.uniprotData.set(uniprotData);
              this.isBiologicalFunctionLoading.set(this.shouldShowBiologicalFunctionLoader(uniprotData));

              if (this.data && this.entryData().isComplex !== true) {
                this.uniprotSummaryData();
              }
            });
    
    if (this.platform.isBrowserPlatform()) {
      this.hydrationReady = true;
    }
  }

  private shouldShowBiologicalFunctionLoader(uniprotData: any): boolean {
    if (!this.entryData()?.isUniProt) {
      return false;
    }

    if (!uniprotData) {
      return true;
    }

    if (uniprotData.__status === 'not_found' || uniprotData.__status === 'error') {
      return false;
    }

    return !uniprotData.accession;
  }

  public summaryGroup(arr: any[], size: number): any[][] {

    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  public onRelatedEntryClick(linkType: string): void {
    this.gaService.emitRelatedEntryClick(linkType, this.entryData()?.isComplex, this.entryData()?.assemblyType);
  }

  getShortenedText(text: string): string {
    if (!text) {
      return;
    }
    const words = text.split(' ');
    if (words.length <= this.data.text_limit) {
      return text;
    }
    const wordSubset = words.slice(0, this.data.text_limit);
    return wordSubset.join(' ') + ' ...';
  }

  getShortenedOrganismNames(text: string): string {
    if (!text) {
      return '';
    }
    if (text.length <= 60) {
      return text;
    }
    return text.substring(0, 60) + ' ...';
  }

  // Check if "Show more" link is needed
  checkOrganismHasToShowMore(text: SummaryText): boolean {
    return !text.show_long && text.text?.length > 60;
  }

  public openedAccordion(data) {
    if (data.type !== 'macromolecules' && this.subSelectionOptions().length > 0) {
      this.clearAccordionSelection(true);
    }
  }

  async toggleAccordion(data) {
    await this.applyMolstarColoring(data.type);
  }

  async toggleAccordionSelection(data) {
    if (data.selections.length === 0) {
      this.clearAccordionSelection(true);
    }
    else {
      this.subSelectionOptions.set(data.selectionNames);
      this.subSelectionData.set(data.selections);
      await this.toggleAccordionSubSelection(0);
    }
  }

  clearAccordionSelection(reset3D?: boolean) {
    this.subSelectionData.set([]);
    this.subSelectionOptions.set([]);
    this.currentSelection.set(null);
    // Clear the selection in Mol*
    if (reset3D) this.pdbeMolstar.visual.clearSelection();
  }

  async onZoomOut(durationMs: number) {
    const instance = this.pdbeMolstar ?? null;
    if (!instance) return;
    this.zoomSelectionMutex = this.zoomSelectionMutex.then(async () => {
      const plugin = instance.plugin ?? null;
      if (!plugin) return;

      await plugin.managers.structure.focus.clear();
      await plugin.managers.camera.reset(undefined, durationMs);
    });
    await this.zoomSelectionMutex;
  }

  async onZoomInAndSelect(molstarSelection: any) {
    const instance = this.pdbeMolstar ?? null;
    if (!instance) return;
    this.zoomSelectionMutex = this.zoomSelectionMutex.then(async () => {
      await this.pdbeMolstar.visual.select(molstarSelection['select']);
      await this.pdbeMolstar.visual.focus(molstarSelection['zoom']);
    });
    await this.zoomSelectionMutex;
  }

  async zoomInCurrentSelection() {
    const index = this.subSelectionOptions().indexOf(this.currentSelection()!);
    const molstarSelection = this.subSelectionData()[index];
    await this.pdbeMolstar.visual.focus(molstarSelection['zoom']);
  }

  async toggleAccordionSubSelection(index: number) {
    this.currentSelection.set(this.subSelectionOptions()[index]);
    const durationMs = this.pdbeMolstar ? this.zoomOutDuration : 0;
    await this.onZoomOut(durationMs);

    timer(durationMs + 100).subscribe(async () => {
      await this.onZoomInAndSelect(this.subSelectionData()[index]);
    });
  }

  toggleUpdateFlags(data) {
    this.isAlphaMis = data.isAlphaMis;
    this.isModalConf = data.isModalConf;
  }

  /**
   * Toggle if a text should be shown completely or only in a shortened
   * version
   * @param text - a text object that has a .show_long attribute
   */
  switchText(text: SummaryText): boolean {
    text.show_long = !text.show_long;
    return text.show_long;
  }

  /**
   * Check if a text is longer than the limit
   * @param text - a text object
   */
  checkIfHasToShowMore(text: SummaryText): boolean {
    const words = text.text.split(' ');
    return !text.show_long && words.length >= this.data.text_limit;
  }

  toggleInfoSection(): void {
    if (this.showInfoSection) {
      this.infoSectionHeight = Object.assign(
        {},
        {
          maxHeight: '0px',
          marginTop: '0px',
          paddingBottom: '0px',
          overflow: 'hidden',
        }
      );
    } else {
      this.infoSectionHeight = Object.assign(
        {},
        { maxHeight: '1000px', paddingBottom: '16px', overflow: 'hidden' }
      );
    }
    this.showInfoSection = !this.showInfoSection;
  }

  applyMolstarColoring(kind: 'plddt' | 'missense') {
    if (kind === 'missense') {
      this.isAlphaMis = true;
      this.isModalConf = false;
      // colorByAlphaMissense(this.pdbeMolstar, this.csvAmData);
      if (this.rangeFilter) {
        colorByAlphaMissense(
          this.pdbeMolstar,
          this.csvAmData(),
          this.rangeFilter
        );
      } else {
        colorByAlphaMissense(this.pdbeMolstar, this.csvAmData());
      }
      applyAlphaMissenseTooltips(this.pdbeMolstar, this.csvAmData());
      this.gaService.eventEmitter(
        'AM_toggle',
        'AlphaMissense',
        'click',
        'AM_toggle',
        'Toggle between AM and pLDDT colouring on Mol*'
      );
    } else if (kind === 'plddt') {
      this.isAlphaMis = false;
      this.isModalConf = true;
      resetColoring(this.pdbeMolstar);
      resetTooltips(this.pdbeMolstar);
    } else if (kind === 'ted') {
      this.setUpTEDToggleUI();
      colorByTed(this.pdbeMolstar, this.tedDomainData());
      this.gaService.eventEmitter(
        'domains_toggle',
        'domains',
        'click',
        'domains_toggle',
        'Toggle between TED colouring on Mol*'
      );
    } else {
      console.error('Unknow coloring kind:', kind);
    }
  }
  private setUpTEDToggleUI(): void {
    this.isAlphaMis = false;
    this.isModalConf = false;
    this.isTedDomain.set(true);
  }

  async clearSeqSelection(): Promise<void> {
    await this.pdbeMolstar.visual.clearSequenceColor();
  }

  validateBlankSelection(regions) {
    regions.forEach((select) => {
      if (select.start === 0 && select.end === 0) {
        this.clearSeqSelection();
        this.pdbeMolstar.visual.reset({ camera: true });
      }
    });
    return;
  }

  cmSelectionUpdate(cmSelectedRegions: PaeSelectedRegions): void {
    // PAE also emits an empty region when it first loads; that must not wipe a macromolecule the
    // user already selected. Only a real brush, or clearing one, should reset the selection.
    if (!cmSelectedRegions && !this.paeSelectionActive) return;
    this.paeSelectionActive = !!cmSelectedRegions;
    this.handleCmUpdate(cmSelectedRegions);
    this.clearAccordionSelection(false);
    this.legendsPanel.clearMacromoleculeSelection();
  }

  async handleCmUpdate(regions: PaeSelectedRegions): Promise<void> {
    if (!this.molLoadComplete) {
      return;
    }

    if (!regions) {
      await this.applyMolstarColoring(this.isAlphaMis ? 'missense' : 'plddt');
      await this.clearSeqSelection();
      this.pdbeMolstar.visual.reset({ camera: true });
      return;
    }

    // const localProteinChains: Chain[] = 
    // [
    //   { name: 'ProteinA', label_asym_id: undefined, length: this.entryData().sequenceEnd, sequenceEnd:this.entryData().sequenceEnd, sequenceStart:1 },
    // ]; // TODO get real data from somewhere
    const selectionData = this.utilService.getSelectionData(regions, this.proteinChains());

    await this.pdbeMolstar.visual.select({
      data: selectionData,
      nonSelectedColor: '#707372',
      keepRepresentations: true,
    });
    await this.pdbeMolstar.visual.sequenceColor({
      data: selectionData,
    });

    // this.applySeqSelection(selectionData);
  }

  initMolstar() {
    this.structure = {
      model_url: this.entryData().bcifUrl,
      // model_url: 'http://localhost:4200/assets/newMolstarbcif.bcif',
      model_format: 'cif',
      binary: true,
    };

    // Set last updated date
    const modelCreatedDate = new Date(this.entryData().modelCreatedDate);
    this.lastUpdateDate = `${modelCreatedDate.getDate()} ${modelCreatedDate.toLocaleString(
      'default',
      { month: 'long' }
    )} ${modelCreatedDate.getFullYear()}`;
    setTimeout(() => {
      this.handleMolstar(this.structure);
    }, 100);
  }

  async handleMolstar(structure: Structure): Promise<void> {
    const options = {
      customData: {
        url: structure.model_url,
        format: structure.model_format,
        binary: structure.binary,
      },
      subscribeEvents: false,
      bgColor: { r: 255, g: 255, b: 255 },
      alphafoldView: true,
      reactive: true,
      sequencePanel: true,
      hideCanvasControls: ['animation'],
      landscape: true,
      rightPanel: true,
      // leftPanel: false,
      hideControls: false,
      validationAnnotation: true,
      granularity: 'residue'
    };

    if (this.pdbeMolstar) {
      this.pdbeMolstar.visual.update(options, true);
    } else {
      const ele = this.el.nativeElement.querySelector('.summary-molstar-container');
      const eleSeq = this.el.nativeElement.querySelector('.summary-sequencepanel');

      if (ele) {
        // this.pdbeMolstar = new PDBeMolstarPlugin();
        await this.molstarPluginService.loadPlugin();
        const pluginInstance = this.molstarPluginService.createInstance();
        this.pdbeMolstar = pluginInstance;
        const layout = [
          { target: ele, component: this.molstarPluginService.getClass().UIComponents.FullLayoutNoControlsUnlessExpanded },
          { target: eleSeq, component: this.molstarPluginService.getClass().UIComponents.SequenceView, props: { defaultMode: 'polymers'  }},
        ]
        this.pdbeMolstar.render(layout, options);
        // this.pdbeMolstar.render(ele, options);

        // Update Mol load complete flag
        this.pdbeMolstar.events.loadComplete.subscribe((e) => {
          this.molLoadComplete = true;
        });
      }
    }
  }


  getExperimentalStructuresText() {
     //Get structure count in PDBe-KB
    const apiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${this.complexData().uniprotAccession[0]}&wt=json`;
    this.commonService.getApiData(apiUrl).subscribe(apiData => {
      if(apiData && apiData.grouped && apiData.grouped.pdb_id) {
        this.complexKBCount = apiData.grouped.pdb_id.ngroups;
      }
    });
  }

  private loadAlsoFoundInCount(accession: string): void {
    if (!accession) {
      this.isAlsoFoundInLoading.set(false);
      this.alsoFoundInCount.set(null);
      return;
    }

    const cached = this.alsoFoundInCache.get(accession);
    if (cached !== undefined) {
      this.isAlsoFoundInLoading.set(false);
      this.alsoFoundInCount.set(cached);
      return;
    }

    this.activeAlsoFoundInAccession = accession;
    this.isAlsoFoundInLoading.set(true);

    const query = encodeURIComponent(`(text:${accession.trim()}*)`);
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : '';
    const searchApiUrl = `${this.configService.getConfig().searchApiUrl}?q=${query}&type=main&start=0&rows=1${auth}`;

    this.commonService.getApiData(searchApiUrl, { isInternal: true }).subscribe({
      next: (apiData) => {
        if (this.activeAlsoFoundInAccession !== accession) return;
        const numFound = typeof apiData?.numFound === 'number' ? apiData.numFound : 0;
        this.alsoFoundInCache.set(accession, numFound);
        this.alsoFoundInCount.set(numFound);
        this.isAlsoFoundInLoading.set(false);
      },
      error: () => {
        if (this.activeAlsoFoundInAccession !== accession) return;
        this.alsoFoundInCount.set(0);
        this.isAlsoFoundInLoading.set(false);
      },
    });
  }

  getCuratedComplexText() {    const accessions = (this.complexData()?.uniprotAccession || []).filter(Boolean);

    if (!accessions.length) {
      this.curatedComplexId = '';
      return;
    }

    const proteins = accessions.join(',');
    // const tempProteins = 'O76406,Q9N3R7,Q95QB8,Q8MNT1';
    const apiUrl = `${this.configService.getConfig().complexIntactApiUrl}find?proteinAcs=${proteins}`;

    this.commonService.getApiData(apiUrl).pipe(
      map((apiData) => {
        const matches = Array.isArray(apiData?.exactMatches) ? apiData.exactMatches : [];
        const firstMatch = matches.find((match) => !!match?.complexAc);
        return firstMatch?.complexAc || '';
      }),
      catchError((error) => {
        console.error('Error fetching curated complex data:', error);
        return of('');
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((complexAc: string) => {
      this.curatedComplexId = complexAc;
    });
  }

  getCuratedComplexPortalUrl(): string {
    const apiUrl = `${this.configService.getConfig().complexPortalUrl}`;
    return this.curatedComplexId ? `${apiUrl}${this.curatedComplexId}` : '';
  }

  getPdbeData() {
    if (this.entryData().isUniProt === true) {
      const accessionName = this.entryData().uniprotAccession;

      // this is the url which we ther from start
      // const solrApiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&group.facet=true&facet=true&rows=0&facet.limit=5&facet.offset=0&facet.mincount=1&facet.sort=count&omitHeader=true&json.nl=map&facet.field=uniprot_accession&facet.threads=-1&q=uniprot_accession:${accessioName}&wt=json`;

      // below is the url shared by Sameer 
      const solrApiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${accessionName}&wt=json`;

      this.commonService.getApiData(solrApiUrl).pipe(
        map((apiData) => {
          if (apiData && apiData.grouped && apiData.grouped.pdb_id) {
            this.kbCount = apiData.grouped.pdb_id.ngroups;
            this.uniprotSummaryData();
          }
        }),
        catchError((error) => {
          console.error('Error fetching pdbe data:', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),

      ).subscribe();
    } else {
      this.kbCount = 0;
      this.uniprotSummaryData();
    }
  }

  createSummaryData(): void {
    const dataItem = this.entryData();

    // const tempOtherOrgamism = ["Tyrannosaurus rex","Arabidopsis thaliana","Herrania umbratica"];
    this.otherOrganismScientificNames = {
      text: dataItem.otherOrganismScientificNames ? dataItem.otherOrganismScientificNames.join(", ") : "", //tempOtherOrgamism.join(", "),
      totalCount: dataItem.otherOrganismScientificNames ? dataItem.otherOrganismScientificNames.length : 0,  //tempOtherOrgamism.length,
      italic: false,
      show_long: false,
      source: '',
      source_url: '',
    }
    const summary = {
      title: '',
      subtitle: '',
      texts: [],
      text_limit: 20,
      flags: [],
      isUniProtReferenceProteome: false,
      isUniProtReviewed: false,
      afdb_accession: ''
    };

    summary.title = dataItem.uniprotDescription;
    summary.subtitle = 'AlphaFold structure prediction';
    summary.isUniProtReferenceProteome = dataItem.isUniProtReferenceProteome;
    summary.isUniProtReviewed = dataItem.isUniProtReviewed;
    summary.afdb_accession = `${dataItem.modelEntityId}-v${dataItem.latestVersion}`;

    if (dataItem.modelEntityId) {
      summary.texts.push({
        label: 'Protein',
        text: dataItem.uniprotDescription ? dataItem.uniprotDescription : 'Not named',
        italic: false,
        show_long: true,
        source: '',
        source_url: '',
      });
    }
    if (dataItem.modelEntityId) {
      const geneName = dataItem.gene ? dataItem.gene : 'Not named';
      summary.texts.push({
        label: 'Gene',
        text: geneName,
        italic: false,
        show_long: true,
        source: '',
        source_url: '',
      });
    }

    // Add common name after latin name, if available
    if (dataItem.organismScientificName) {
      summary.texts.push({
        label: 'Source organism',
        text: dataItem.organismScientificName,
        searchText: dataItem.organismScientificName,
        italic: true,
        show_long: true,
        source: '',
        source_url: '',
      });
    }
    if (dataItem.uniprotAccession) {
      // A fragment of a larger UniProt protein (residue numbering doesn't start at 1, e.g.
      // one domain cleaved out of a polyprotein) — surface that range next to the accession
      // so it doesn't read as if it covers the whole protein (AFPDBE-1445 follow-up).
      const uniprotRange = this.utilService.getMacromoleculeResidueRange(dataItem);
      const rangeSuffix = uniprotRange && uniprotRange.start !== 1 ? ` [residues: ${uniprotRange.start} – ${uniprotRange.end}]` : '';
      summary.texts.push({
        label: 'UniProt',
        text: dataItem.uniprotAccession + rangeSuffix,
        italic: false,
        show_long: true,
        source: 'UniProt',
        source_url:
          this.configService.getConfig().uniprotEntryUrl +
          dataItem.uniprotAccession,
      });
    }else{
      summary.texts.push({
        label: 'UniProt',
        text: "Data unavailable (protein not indexed in UniProt)",
        italic: false,
        show_long: true,
        source: '',
        source_url:'',
      });
    }
    if (dataItem.globalMetricValue) {
      const averagepLDDT = dataItem.globalMetricValue ? `${dataItem.globalMetricValue} (${this.getPlddtLevel(dataItem.globalMetricValue)})` : 'Not available';
      summary.texts.push({
        label: 'Average pLDDT',
        text: averagepLDDT,
        italic: false,
        show_long: true,
        source: 'tooltip',
        source_url: '',
      });
    }
    if (dataItem.fractionPlddtVeryLow || dataItem.fractionPlddtLow || dataItem.fractionPlddtConfident || dataItem.fractionPlddtVeryHigh) {
      this.plddtData = {
        fractionPlddtVeryLow: this.entryData().fractionPlddtVeryLow,
        fractionPlddtLow: this.entryData().fractionPlddtLow,
        fractionPlddtConfident: this.entryData().fractionPlddtConfident,
        fractionPlddtVeryHigh: this.entryData().fractionPlddtVeryHigh
      };
    }

    this.data = summary;
    this.summaryTexts = this.summaryGroup(this.data.texts.slice(0, 8), this.summaryGroupSize);
    // this.uniprotSummaryData()
  }

  uniprotSummaryData(): void {
    if (!this.data) {
      return;
    }

    const uniprotAccession = Array.isArray(this.entryData().uniprotAccession)
      ? this.entryData().uniprotAccession[0]
      : this.entryData().uniprotAccession;

    const matchingUniprotData =
      this.entryData().isUniProt &&
      this.uniprotData() &&
      this.uniprotData().accession === uniprotAccession
        ? this.uniprotData()
        : null;

    // Check if common name exists in UniProt data
    let commonName = '';
    if (
      matchingUniprotData &&
      matchingUniprotData.organism &&
      matchingUniprotData.organism.names
    ) {
      const names = matchingUniprotData.organism.names;
      const scientificName = names.find((name) => name.type === 'scientific')?.value || '';
      const shouldAppendCommonName =
        !!scientificName &&
        scientificName.trim().toLowerCase() ===
          (this.entryData().organismScientificName || '').trim().toLowerCase();

      names.forEach((name) => {
        if (name.type && name.type === 'common') {
          commonName = name.value;
        }
      });
      const sourceOrganism = this.data.texts.find(t => t.label === 'Source organism');
      if (sourceOrganism) {
        sourceOrganism.text = shouldAppendCommonName && commonName
          ? sourceOrganism.text + ' (' + commonName + ')'
          : sourceOrganism.text;
      }
    }

    // Default biological function text
    const bioFunction: SummaryText = {
      label: 'Biological function',
      text: 'Data unavailable (function not known)',
      italic: false,
      show_long: false,
      source: 'tooltip',
      source_url: '',
      isTooltip: true
    };
    let catalyticActivity: string;
    // Set biological function and catalytic activity
    if (matchingUniprotData && matchingUniprotData.comments) {
      const functionComment = matchingUniprotData.comments.find(
        (comment) => comment.type === 'FUNCTION' && comment.text?.length
      );

      if (functionComment) {
        bioFunction.text = functionComment.text[0].value;
        bioFunction.text += '.';
        bioFunction.source = 'UniProt'
        bioFunction.source_url = "https://www.uniprot.org/uniprot/" + matchingUniprotData.accession;
      }

      matchingUniprotData.comments.forEach((comment) => {
        // if (comment.type === 'FUNCTION') {
        //   bioFunction.text = comment.text[0].value;
        // }
        if (comment.type === 'CATALYTIC_ACTIVITY') {
          catalyticActivity = comment.reaction.name;
        }
      });
    }

    if (bioFunction.text === 'Data unavailable (function not known)' && catalyticActivity !== undefined) {
      bioFunction.text = 'Catalytic activity: ' + catalyticActivity;
    }

    if (bioFunction.text !== 'Data unavailable') {
      const pubmed = 'PubMed:([0-9]+)';
      const re = new RegExp(pubmed, 'g');
      bioFunction.text = bioFunction.text.replace(
        re,
        '<a href="https://www.uniprot.org/citations/$1" target="_blank">PubMed:$1</a>'
      );
    }

    // Add KB data
    const kbText =
      this.kbCount === 0
        ? 'None available in the PDB'
        : `${this.kbCount} structure${this.kbCount > 1 ? 's' : ''} in PDB for ${matchingUniprotData?.accession || uniprotAccession}`;
    const kbSource = this.kbCount === 0 ? '' : 'PDBe-KB';
    const kbUrl =
      this.kbCount === 0
        ? ''
        : this.configService.getConfig().pdbeKbEntryUrl + (matchingUniprotData?.accession || uniprotAccession);
    const kbdata = {
      label: 'Experimental structures',
      text: kbText,
      italic: false,
      show_long: true,
      source: kbSource,
      source_url: kbUrl,
    };

    const kbidx = this.data.texts.findIndex((o) => o.label === 'Experimental structures');

    if (kbidx !== -1) {
      // remove from old position
      this.data.texts.splice(kbidx, 1);
    }


    const bioidx = this.data.texts.findIndex((o) => o.label === 'Biological function');
    if (bioidx !== -1) {
      this.data.texts.splice(bioidx, 1, bioFunction);
    } else {
      this.data.texts.splice(4, 0, bioFunction);
    }

    // insert at 6th position (index 5)
    this.data.texts.splice(5, 0, { ...kbdata });
    this.summaryTexts = this.summaryGroup(this.data.texts.slice(0, 8), this.summaryGroupSize);
  }

  public copySequence(sequence) {
    // const text = `${sequence}`;
    const text = this.getChainWiseSequenceText(`${sequence || ''}`);
    this.utilService.copy(text);
    this.isCopySequence = true;
    setTimeout(() => {
      this.isCopySequence = false;
    }, 3000);
    this.gaService.eventEmitter('sequence_copy', 'interaction', 'click', "copy_sequence", "Click on Copy sequence");
  }

  private getChainWiseSequenceText(fullSequence: string): string {
    const sequence = `${fullSequence || ''}`;
    const chains = (this.proteinChains() || []).filter((chain) => !!chain);

    if (!sequence || chains.length <= 1) {
      return sequence;
    }

    const chainBlocks = chains
      .slice()
      .sort((a, b) => (a.sequenceStart || 0) - (b.sequenceStart || 0))
      .map((chain, index) => {
        const chainLabel = chain.label_asym_id || `${index + 1}`;
        // Each chain has its own sequence and its own 1-based numbering — sequenceStart/
        // sequenceEnd here are that chain's own PAE-matrix range, not a position within
        // one shared multi-chain string, so slicing `sequence` by them produced garbage
        // for every chain except whichever one `sequence` itself happened to equal
        // (AFPDBE-1418). Use each chain's own row from macromoleculesData instead.
        const macromolecule = this.macromoleculesData()?.find(
          (item) => ((item as any)?.chainId || '').trim() === chainLabel
        );
        const chainSequence = macromolecule?.sequence || sequence;
        const chainName = chain.name ? ` (${chain.name})` : '';
        return `Chain ${chainLabel}${chainName}\n${chainSequence}`;
      })
      .filter((block) => !!block.trim());

    return chainBlocks.length > 0 ? chainBlocks.join('\n\n') : sequence;
  }

  public getPlddtLevel(plddt: number): string {
    return plddt > 90 ? 'Very high' :
      plddt > 70 ? 'High' :
        plddt > 50 ? 'Low' :
          'Very low';
  }
  // Very high (ipSAE ≥ 0.8) – The predicted homodimer interface is modelled with high accuracy.
  // Confident (0.8 > ipSAE ≥ 0.7) – The predicted interface is likely correct with a well-arranged interaction.
  // Low (0.7 > ipSAE ≥ 0.6) – The interaction may be correct but should be interpreted cautiously.
  // Very low (ipSAE < 0.6) 
  public getipSAELevel(ipSAE: number): string {
    return ipSAE >= 0.8 ? 'Very high' :  
          ipSAE >= 0.7 ? 'Confident' : 
          ipSAE >= 0.6 ? 'Low' :       
          'Very low';                  
  }
}
